<script setup lang="ts">
import Switch from "@/ui/components/switch/switch.vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { useBaseStore } from "@/stores/base";
import DataTableDO = App.Data.Explorer.DataTableDO;
import {
	computed,
	Ref,
	ref,
	shallowRef,
	watch,
	WritableComputedRef,
} from "vue";
import SecondaryDrawerHeader from "@/modules/explorer/components/SecondaryDrawer/SecondaryDrawerHeader.vue";
import SettingsRow from "@/modules/explorer/components/SecondaryDrawer/SettingsRow.vue";
import DocumentDO = App.Data.Explorer.DocumentDO;
import {
	getCurrentDataTable,
	updateCurrentDataTable,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import { toRawDeep } from "@/Utils/to-raw-deep";
import SettingsRowInput from "@/modules/explorer/components/SecondaryDrawer/SettingsRowInput.vue";
import { hierarchyNamingStyleOptions } from "@/modules/explorer/components/SecondaryDrawer/displaySettingsOptions";

const baseStore = useBaseStore();

const document = computed<DocumentDO>(() => useExplorerStore().document);
const derivedDocument = shallowRef<DocumentDO>(document.value);

const dataTable: WritableComputedRef<DataTableDO> = getCurrentDataTable(
	document.value,
	derivedDocument.value
);
const dataTableToEdit = ref(
	structuredClone(toRawDeep<DataTableDO>(dataTable.value))
) as Ref<DataTableDO>;

const currentSplitPrediction = computed(
	() => dataTableToEdit.value.split_prediction
);
const initialSplitPrediction = shallowRef(
	dataTableToEdit.value.split_prediction
);

const currentComparisonNested = computed(
	() => dataTableToEdit.value.is_comparison_nested
);
const initialComparisonNested = shallowRef(
	dataTableToEdit.value.is_comparison_nested
);

const splitPredictionChanged = computed(() => {
	return currentSplitPrediction.value !== initialSplitPrediction.value;
});

const isComparisonNestedChanged = computed(() => {
	return currentComparisonNested.value !== initialComparisonNested.value;
});

const hasHiddenColumns = computed(() => {
	return dataTableToEdit.value.hidden_columns.length > 0;
});

const shouldShowZeroValuesForDateTime = ref(
	document.value.should_show_zero_values_for_date_time
);

const shouldShowTotalValuesForEachColumn = ref(
	document.value.data_tables[0].show_total_values
);

const shouldShowRowNumbers = ref(
	document.value.data_tables[0].show_row_numbers
);

const hierarchyNamingStyle = ref(document.value.hierarchy_naming_style);

const openExpansionPanels = ref([0, 1, 2]);

const disableRelativeComparison = computed(
	() =>
		dataTableToEdit.value.is_comparison_nested &&
		!dataTableToEdit.value.show_change_absolute
);

const disableAbsoluteComparison = computed(
	() =>
		dataTableToEdit.value.is_comparison_nested &&
		!dataTableToEdit.value.show_change_relative_to_current
);

watch(
	() => dataTableToEdit.value.is_comparison_nested,
	(value) => {
		if (
			value &&
			!dataTableToEdit.value.show_change_relative_to_current &&
			!dataTableToEdit.value.show_change_absolute
		) {
			dataTableToEdit.value.show_change_relative_to_current = true;
		}
	}
);

const saveOptions = () => {
	updateCurrentDataTable(
		document.value,
		derivedDocument.value,
		dataTableToEdit.value
	);
	document.value.should_show_zero_values_for_date_time =
		shouldShowZeroValuesForDateTime.value;
	document.value.data_tables[0].show_total_values =
		shouldShowTotalValuesForEachColumn.value;
	document.value.data_tables[0].show_row_numbers = shouldShowRowNumbers.value;

	document.value.hierarchy_naming_style = hierarchyNamingStyle.value;
	if (
		hasHiddenColumns.value &&
		(splitPredictionChanged.value || isComparisonNestedChanged.value)
	) {
		document.value.data_tables[0].hidden_columns = [];
	}

	baseStore.showSecondaryDrawer = false;
};
</script>

<template>
	<SecondaryDrawerHeader icon="mdi-tune" title="Display Settings" />

	<v-expansion-panels v-model="openExpansionPanels" multiple>
		<v-expansion-panel
			class="mt-0 expansion-panel-border"
			elevation="0"
			:key="'yoy'"
		>
			<v-expansion-panel-title class="setting-header-format">
				<span>Year Over Year Comparison</span>
				<v-spacer />
			</v-expansion-panel-title>

			<v-expansion-panel-text>
				<SettingsRow title="Condense into one column">
					<Switch v-model="dataTableToEdit.is_comparison_nested" hide-details />
				</SettingsRow>

				<SettingsRow title="Show relative difference">
					<Switch
						v-model="dataTableToEdit.show_change_relative_to_current"
						:disabled="disableRelativeComparison"
						hide-details
					/>
				</SettingsRow>

				<SettingsRow title="Show absolute difference">
					<Switch
						v-model="dataTableToEdit.show_change_absolute"
						:disabled="disableAbsoluteComparison"
						hide-details
					/>
				</SettingsRow>
			</v-expansion-panel-text>
		</v-expansion-panel>

		<v-expansion-panel
			class="mt-0 expansion-panel-border"
			elevation="0"
			:key="'prediction'"
		>
			<v-expansion-panel-title class="setting-header-format">
				<span>Predictions</span>
				<v-spacer />
			</v-expansion-panel-title>

			<v-expansion-panel-text>
				<SettingsRow title="Split into separate column">
					<Switch v-model="dataTableToEdit.split_prediction" hide-details />
				</SettingsRow>
			</v-expansion-panel-text>
		</v-expansion-panel>

		<v-expansion-panel
			class="mt-0 expansion-panel-border"
			elevation="0"
			:key="'other'"
		>
			<v-expansion-panel-title class="setting-header-format">
				<span>Other Settings</span>
				<v-spacer />
			</v-expansion-panel-title>

			<v-expansion-panel-text>
				<SettingsRow title="Show zero values for dates">
					<Switch v-model="shouldShowZeroValuesForDateTime" hide-details />
				</SettingsRow>

				<SettingsRow title="Show total values for each column">
					<Switch v-model="shouldShowTotalValuesForEachColumn" hide-details />
				</SettingsRow>

				<SettingsRow title="Show row numbers">
					<Switch v-model="shouldShowRowNumbers" hide-details />
				</SettingsRow>

				<SettingsRow title="Hierarchy naming style" />
				<settings-row-input class="setting-details-format">
					<v-select
						density="compact"
						v-model="hierarchyNamingStyle"
						:items="hierarchyNamingStyleOptions"
						item-title="name"
						item-value="id"
						variant="filled"
						hide-details
					/>
				</settings-row-input>
			</v-expansion-panel-text>
		</v-expansion-panel>
	</v-expansion-panels>

	<v-divider />

	<div class="d-flex justify-end ma-2 ga-2">
		<v-btn variant="text" @click="baseStore.showSecondaryDrawer = false">
			Close
		</v-btn>

		<v-btn @click="saveOptions"> Save </v-btn>
	</div>

	<v-alert
		v-if="hasHiddenColumns && splitPredictionChanged"
		type="warning"
		class="pa-4 ma-2"
	>
		Toggling split prediction will reset all your current hidden columns
	</v-alert>

	<v-alert
		v-if="hasHiddenColumns && isComparisonNestedChanged"
		type="warning"
		class="pa-4 ma-2"
	>
		Toggling condense year-over-year comparison into one column will reset all
		your current hidden columns
	</v-alert>
</template>

<style scoped lang="scss"></style>
